import React from 'react';
import { SandpackProvider as SP } from '@codesandbox/sandpack-react';
import { SandboxErrorBoundary } from './SandboxErrorBoundary';
import { SandboxErrorReporter } from './SandboxErrorReporter';
import type { SandboxErrorHandler } from './SandboxErrorBoundary';
import { getSandboxErrorHandler } from './sandboxErrorConfig';

export interface SandboxProviderProps {
  files: Record<string, string | { code: string; readOnly?: boolean }>;
  visibleFiles?: string[];
  externalResources?: string[];
  style?: React.CSSProperties;
  /** Called on both infrastructure failures and user JSX runtime errors.
   *  Falls back to the global handler set via setSandboxErrorHandler(). */
  onError?: SandboxErrorHandler;
  children: React.ReactNode;
}

/**
 * Composable sandbox provider — wraps SandpackProvider so children can use
 * SandboxCodeEditor, SandboxPreview, and useSandboxContext() without importing
 * from @codesandbox/sandpack-react directly.
 */
export function SandboxProvider({
  files,
  visibleFiles,
  externalResources,
  style,
  onError,
  children,
}: SandboxProviderProps): React.ReactElement {
  const handler = onError ?? getSandboxErrorHandler() ?? undefined;

  return (
    <SandboxErrorBoundary onError={handler}>
      <SP
        template="react"
        files={files}
        options={{
          visibleFiles,
          recompileMode: 'delayed',
          recompileDelay: 500,
          externalResources,
        }}
        style={style}
      >
        {children}
        <SandboxErrorReporter onError={handler} />
      </SP>
    </SandboxErrorBoundary>
  );
}
